import React from "react";
import BaseModal from "@/components/common/HPBaseModal";
import { ModalProps } from "@_constants/modal";
import { type TabData } from "@utils/dashboard-storage";
import type { DashboardItem } from "@/models/dashboard";
import { useAppDispatch } from "@/store";
import { modalDispatcher } from "@/store/slices/modal-slice";
import { useCopyMoveWidget } from "@/hooks/copy-move-widget/use-copy-move-widget";
import DestinationTabList from "./copy-move-widget/DestinationTabList";

const WIDGET_TYPE_LABELS: Record<DashboardItem["type"], string> = {
  text: "Text Widget",
  image: "Image Widget",
  chart: "Visualization",
};

interface CopyMoveWidgetModalProps extends ModalProps {
  mode: "copy" | "move";
  dashboardId: string;
  sourceTabId: string;
  sourceTabName: string;
  widgetId: string;
  widgetType: DashboardItem["type"];
  widgetTitle: string;
  tabs: TabData[];
  onViewTab: (
    targetTabId: string,
    targetTabLabel: string,
    widgetId: string,
  ) => void;
}

const CopyMoveWidgetModal: React.FC<CopyMoveWidgetModalProps> = ({
  mode,
  dashboardId,
  sourceTabId,
  sourceTabName,
  widgetId,
  widgetType,
  widgetTitle,
  tabs,
  onViewTab,
}) => {
  const dispatch = useAppDispatch();
  const { closeTopModal } = modalDispatcher(dispatch);

  const {
    selectedTabId,
    setSelectedTabId,
    isSubmitting,
    error,
    handleConfirm,
  } = useCopyMoveWidget({ mode, dashboardId, sourceTabId, widgetId, onViewTab });

  const destinationTabs = tabs.filter((tab) => tab.id !== sourceTabId);
  const selectedTab = destinationTabs.find((tab) => tab.id === selectedTabId);

  const widgetLabel =
    widgetTitle && widgetTitle.trim() && widgetTitle !== "Widget"
      ? widgetTitle
      : `Untitled ${WIDGET_TYPE_LABELS[widgetType]}`;

  const actionVerb = mode === "copy" ? "Copy" : "Move";
  const progressVerb = mode === "copy" ? "Copying..." : "Moving...";

  return (
    <BaseModal
      title={`${actionVerb} widget to another tab`}
      message={
        <>
          <span>
            <strong>{widgetLabel}</strong> is currently on{" "}
            <strong>{sourceTabName}</strong>.
            {mode === "move" &&
              " It will leave this tab after a successful move."}
          </span>
          <div className="mt-3">
            <DestinationTabList
              tabs={destinationTabs}
              selectedTabId={selectedTabId}
              onSelect={setSelectedTabId}
            />
          </div>
          {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
        </>
      }
      confirmButtonText={
        isSubmitting ? progressVerb : `${actionVerb} widget`
      }
      cancelButtonText="Cancel"
      confirmDisabled={!selectedTabId || isSubmitting}
      loading={isSubmitting}
      onConfirm={() => selectedTab && handleConfirm(selectedTab.label)}
      onCancel={closeTopModal}
    />
  );
};

export default CopyMoveWidgetModal;
